import { ResourcesPermissionsV2 } from '../constants/PermissionsV2'
import { fetchPermissionsV2 } from '../services/permissions/permissionsV2.service'

const CUSTOMER_ID_KEY = 'cop:customer-id'

// Reveals a permitted tab's nav control (overrides the stylesheet's default
// `display: none` on the tab nav item).
const GRANTED_CLASS = 'cop-doc-tab-granted'
// Force-hides a denied tab's panel so a denied default-active tab can't leak its
// content. Panel visibility for permitted tabs is left to the Tabs component.
const DENIED_CLASS = 'cop-doc-tab-denied'

interface DocumentationTab {
    tabId: string
    panelId: string
    permission: ResourcesPermissionsV2
}

// Maps each Resources > Documentation tab (its nav control + panel) to the
// permission that grants access.
const DOCUMENTATION_TABS: DocumentationTab[] = [
    {
        tabId: 'tab-cop-resources-doc-ied',
        panelId: 'panel-cop-resources-doc-ied',
        permission: ResourcesPermissionsV2.DocumentationViewIed,
    },
    {
        tabId: 'tab-cop-resources-doc-rtu',
        panelId: 'panel-cop-resources-doc-rtu',
        permission: ResourcesPermissionsV2.DocumentationViewRtu,
    },
    {
        tabId: 'tab-cop-resources-doc-wired',
        panelId: 'panel-cop-resources-doc-wired',
        permission: ResourcesPermissionsV2.DocumentationViewWcn,
    },
]

function isGraniteAuthor(): boolean {
    return Boolean((window as unknown as { Granite?: { author?: unknown } }).Granite?.author)
}

function presentTabs(): DocumentationTab[] {
    return DOCUMENTATION_TABS.filter(
        tab => document.getElementById(tab.tabId) !== null || document.getElementById(tab.panelId) !== null,
    )
}

// Reveal the tab's nav control. The panel is intentionally NOT touched — the Tabs
// component owns panel show/hide so only the active panel is shown. Forcing the
// panel visible would override that and stack every panel under one tab.
function grant(tab: DocumentationTab): void {
    document.getElementById(tab.tabId)?.classList.add(GRANTED_CLASS)
}

// Deny access: the nav control stays hidden (CSS default) and the panel is
// force-hidden in case it is the authored default-active panel.
function deny(tab: DocumentationTab): void {
    document.getElementById(tab.panelId)?.classList.add(DENIED_CLASS)
}

async function init(): Promise<void> {
    const tabs = presentTabs()

    // Not on a page that renders any documentation tab.
    if (tabs.length === 0) {
        return
    }

    // Author mode: keep every tab visible so editors can see and edit them.
    if (isGraniteAuthor()) {
        tabs.forEach(tab => grant(tab))
        return
    }

    const customerId = sessionStorage.getItem(CUSTOMER_ID_KEY) ?? ''
    if (!customerId) {
        // No customer context → deny everything.
        tabs.forEach(tab => deny(tab))
        return
    }

    try {
        const data = await fetchPermissionsV2(customerId)
        const grantedPermissions = new Set(data.permissions.map(p => p.name))
        tabs.forEach(tab => {
            if (grantedPermissions.has(tab.permission)) {
                grant(tab)
            } else {
                deny(tab)
            }
        })
    } catch {
        // On failure deny everything.
        tabs.forEach(tab => deny(tab))
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        void init()
    })
} else {
    void init()
}
